import { AlertTriangle } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { Section, Empty } from "../common";
import { useT } from "../../lib/i18n";
import { money, fdate } from "../../lib/format";

export const TaxStatus = ({ status }) => {
  const { t } = useT();
  const tone = status === "validated" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : status === "suspended" ? "bg-neutral-100 text-neutral-500 border-neutral-300" : "bg-amber-50 text-amber-800 border-amber-300";
  return <span className={`rounded-sm border px-1.5 py-0.5 text-[11px] font-semibold ${tone}`}>{t(`tax.st.${status}`)}</span>;
};

export default function FiscalTab({ q, d, set, m, locked }) {
  const { t } = useT();
  const sel = new Set(d.tax_rule_ids || []);
  const toggle = (id, c) => { const s = new Set(sel); c ? s.add(id) : s.delete(id); set({ tax_rule_ids: [...s] }); };
  return (
    <div className="grid gap-4 xl:grid-cols-2 [&>*]:min-w-0">
      <Section title={`${t("ws.taxRules")} · ${d.country || "—"}`} testid="section-tax-rules">
        <div className="mb-3 flex gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"><AlertTriangle className="h-4 w-4 shrink-0" />{t("ws.taxWarn")}</div>
        <table className="dense-table w-full">
          <thead><tr><th>{t("ws.apply")}</th><th>{t("common.name")}</th><th>{t("ws.rateCol")}</th><th>{t("tax.appliesTo")}</th><th>{t("common.status")}</th><th>v</th><th>{t("cat.validFrom")}</th></tr></thead>
          <tbody>
            {(m.taxRules || []).map((r) => (
              <tr key={r.id} data-testid={`tax-rule-row-${r.id}`}>
                <td><Checkbox data-testid={`tax-rule-toggle-${r.name.replace(/\s+/g, "-").toLowerCase()}`} disabled={locked} checked={sel.has(r.id)} onCheckedChange={(c) => toggle(r.id, c)} /></td>
                <td className="font-medium">{r.name}<div className="text-[10px] text-neutral-500">{r.source}</div></td>
                <td className="num">{r.tax_type === "withholding" ? "−" : ""}{r.rate}%</td>
                <td>{(r.applies_to || []).map((a) => (a === "all" ? t("common.all") : t(`lt.${a}`))).join(", ")}</td>
                <td><TaxStatus status={r.status} /></td>
                <td>{r.version}</td>
                <td>{fdate(r.valid_from)}{r.valid_to ? ` → ${fdate(r.valid_to)}` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!(m.taxRules || []).length && <Empty>{t("common.empty")}</Empty>}
      </Section>
      <Section title={t("ws.taxes")} testid="section-tax-calc">
        <table className="dense-table w-full">
          <thead><tr><th>{t("common.name")}</th><th>{t("ws.rateCol")}</th><th className="text-right">{t("ws.base")}</th><th className="text-right">{t("ws.amount")}</th><th>{t("f.applied")}</th></tr></thead>
          <tbody>
            {q.calc.taxes.map((x) => (
              <tr key={x.rule_id} className={x.applied ? "" : "bg-amber-50/50"}>
                <td>{x.name} <span className="text-neutral-400">v{x.version}</span></td>
                <td className="num">{x.rate}%</td>
                <td className="num">{money(x.base)}</td>
                <td className={`num ${x.applied ? "font-semibold" : "text-neutral-400 line-through"}`}>{money(x.amount)}</td>
                <td>{x.applied ? <span className="text-emerald-700">✓</span> : <span className="text-[11px] text-amber-800">{t(`w.tax_${x.reason}`)}</span>}</td>
              </tr>
            ))}
            <tr><td colSpan={3} className="font-bold">{t("ws.taxes")}</td><td className="num font-bold" data-testid="tax-total">{money(q.calc.totals.tax_total, q.currency)}</td><td /></tr>
          </tbody>
        </table>
        {!q.calc.taxes.length && <Empty>{t("w.no_tax_rule")}</Empty>}
      </Section>
    </div>
  );
}
